import {CAMPAIGN_KEYS} from "@/api/campaignApi";
import {INFLUENCER_KEYS} from "@/api/influencerApi";
import {MEDIA_KEYS} from "@/api/mediaApi";
import {PUNISHMENT_KEYS} from "@/api/punishmentApi";
import type {
  CampaignDashboardSerializer,
  CampaignManagementInfluencerSerializer,
  InfluencerProfileDetailSerializer,
  PunishmentSerializer,
} from "@/generated/DjangoTypes";
import {useGenericInfiniteQueryWithCount} from "@/hooks/useQueryHooks";
import {useSelectedCompanyId} from "@/hooks/useUserHooks";
import useCampaignInfluencerFilterStore from "@/store/campaignInfluencerFilter";
import useCampaignManagementStore, {useManagedCampaignType} from "@/store/campaignManagement";
import useInfluencerNetworkStore from "@/store/influencerNetwork";
import {disciplinaryModalAtom, disciplineTabAtom, isAdminFeedbackModalOpenAtom} from "@/store/jotaiAtoms";
import type {TDisciplineCampaign, TDisciplineTab, TPunishmentType} from "@/store/types";
import {useQueryClient} from "@tanstack/react-query";
import {useLocation, useRouter} from "@tanstack/react-router";
import {useAtom, useAtomValue} from "jotai";
import {useTranslation} from "react-i18next";

const DISCIPLINE_SEVERITY: Record<TPunishmentType, number> = {
  UN_VERIFIED_INSTAGRAM: 0,
  UN_VERIFIED_TIKTOK: 0,
  UN_VERIFIED_UGC: 0,
  PROBATION: 1,
  TIMED_BAN: 2,
  PERMANENT_BAN: 3,
};

const useDiscipline = () => {
  const {t} = useTranslation(["influencer_discipline", "common"]);
  const location = useLocation();
  const router = useRouter();
  const [disciplinaryData, setDisciplineModalData] = useAtom(disciplinaryModalAtom);
  const disciplineTab = useAtomValue(disciplineTabAtom);
  const campaignType = useManagedCampaignType();
  const isFeedbackModalOpen = useAtomValue(isAdminFeedbackModalOpenAtom);
  const tabState = useCampaignManagementStore(state => state.tabState);
  const queryClient = useQueryClient();
  const companyId = useSelectedCompanyId();
  const campaign = useCampaignManagementStore(state => state.campaign);
  const influencerNetworkSearchParams = useInfluencerNetworkStore(state => state.searchParams);
  const searchParam = useCampaignInfluencerFilterStore(state => state.filters.search);

  const {data: activeCampaigns} = useGenericInfiniteQueryWithCount<{
    campaign: CampaignDashboardSerializer;
  }>({
    queryKey: INFLUENCER_KEYS.qInfluencerActiveCampaigns(disciplinaryData?.influencer.id!),
    enabled: !!disciplinaryData?.influencer.id,
  });

  const disciplineTabInPunishmentType: TPunishmentType =
    disciplineTab === "UNVERIFY"
      ? disciplinaryData?.selectedUnverificationType === "instagram"
        ? "UN_VERIFIED_INSTAGRAM"
        : disciplinaryData?.selectedUnverificationType === "tiktok"
          ? "UN_VERIFIED_TIKTOK"
          : "UN_VERIFIED_UGC"
      : disciplineTab;

  const getUnverificationPlatformLabel = (type?: TPunishmentType) => {
    switch (type) {
      case "UN_VERIFIED_INSTAGRAM":
        return t("common:instagram");
      case "UN_VERIFIED_TIKTOK":
        return t("common:tiktok");
      case "UN_VERIFIED_UGC":
        return t("common:ugc");
      default:
        return "";
    }
  };

  const getDisciplinaryModalPreviousLocation = () => {
    if (isFeedbackModalOpen) {
      return t("feedback");
    } else if (tabState === "active") {
      return t("active");
    } else if (tabState === "reporting") {
      return t("reporting");
    } else if (location.pathname.includes("/influencers-list")) {
      return t("influencers");
    } else if (location.pathname.includes("/influencer/")) {
      return t("influencer");
    } else if (location.pathname.includes("/removed/")) {
      return t("removed");
    } else if (location.pathname.includes("/preparation/")) {
      return t("preparation");
    } else {
      return t("back");
    }
  };

  const getLabelByDisciplineType = (disciplineType: TDisciplineTab | TPunishmentType) => {
    if (disciplineType === "PROBATION") {
      return t("probation");
    } else if (disciplineType === "TIMED_BAN") {
      return t("timed_ban");
    } else if (disciplineType === "PERMANENT_BAN") {
      return t("ban");
    }

    return t("unverify_influencer");
  };

  const isStrongerDisciplineActive =
    disciplineTab === "UNVERIFY" || !disciplinaryData.lastDiscipline?.punishment_type
      ? false
      : DISCIPLINE_SEVERITY[disciplinaryData.lastDiscipline?.punishment_type] >
        DISCIPLINE_SEVERITY[disciplineTabInPunishmentType];

  const initializeDisciplineModal = ({
    campaignApplication,
    influencer,
    selectedPunishment,
  }: {
    campaignApplication?: TDisciplineCampaign;
    influencer: CampaignManagementInfluencerSerializer | InfluencerProfileDetailSerializer;
    selectedPunishment?: PunishmentSerializer | null;
  }) => {
    const {user} = influencer;

    setDisciplineModalData({
      campaignApplication,
      influencer: {
        firstName: user.first_name,
        id: influencer.uuid!,
        image: influencer.picture320,
        isInstagramVerified: user.is_verified,
        isTiktokVerified: user.tiktok_verified,
        isUgcVerified: user.ugc_verified,
        username: campaignType === "tiktok" ? user.tiktok_username! : user.username!,
      },
      instagramDiscipline: user.instagram_unverified,
      lastDiscipline: user.last_punishment,
      selectedDiscipline: selectedPunishment,
      tiktokDiscipline: user.tiktok_unverified,
      ugcDiscipline: user.ugc_unverified,
      userId: user.id,
    });
  };

  const invalidateQueries = () => {
    const influencerParams = router.state.location.searchStr.slice(1);
    [
      CAMPAIGN_KEYS.qCampaignActiveApplications(campaign.id, searchParam),
      CAMPAIGN_KEYS.qCampaignCancelledApplications(campaign.id),
      CAMPAIGN_KEYS.qCampaignPreparationApplications(campaign.id, false, searchParam),
      CAMPAIGN_KEYS.qCampaignPreparationApplications(campaign.id, true, searchParam),
      CAMPAIGN_KEYS.qCampaignReportingApplications({campaignId: campaign.id, searchParam}).slice(0, 2),
      INFLUENCER_KEYS.qInfluencer(
        disciplinaryData.influencer.id,
        `${influencerParams ?? ""}${!!influencerParams?.length ? "&" : ""}${!!companyId ? "company_id=" + companyId : ""}`,
      ),
      INFLUENCER_KEYS.qInfluencerList(influencerNetworkSearchParams),
      MEDIA_KEYS.qContentFeedback({campaignId: campaign.id}),
    ].map(queryKey => queryClient.invalidateQueries({queryKey}));
    queryClient.refetchQueries({queryKey: PUNISHMENT_KEYS.qPunishments(disciplinaryData?.userId!)});
  };

  const setDisciplineByTab = (tab: TDisciplineTab) => {
    setDisciplineModalData(draft => {
      draft.selectedDiscipline =
        tab === "UNVERIFY"
          ? undefined
          : disciplinaryData.lastDiscipline?.punishment_type === tab
            ? disciplinaryData.lastDiscipline
            : undefined;
    });
  };

  return {
    activeCampaignsCount: activeCampaigns?.count,
    disciplineTabInPunishmentType,
    getDisciplinaryModalPreviousLocation,
    getLabelByDisciplineType,
    getUnverificationPlatformLabel,
    initializeDisciplineModal,
    invalidateQueries,
    isStrongerDisciplineActive,
    setDisciplineByTab,
  };
};

export {useDiscipline};
